import { Component } from '@theme/component';

/**
 * How far the lazy, low-frequency swing of a segment may stray from the
 * straight line between two dots, as a fraction of the distance between them.
 * This is the big gesture of the stroke. Past about 0.25 it looks erratic.
 * @type {number}
 */
const WANDER = 0.24;

/**
 * Amplitude of the second, faster swing laid over the first, relative to
 * WANDER. Two swings at different rates are what stop a segment reading as one
 * clean arc - but the faster one is what puts kinks in it, so it stays small.
 * @type {number}
 */
const SECONDARY_SWING = 0.26;

/** Bounds on the rate of that secondary swing, in half-cycles per segment. */
const MIN_SWING_RATE = 1;
const MAX_SWING_RATE = 1.8;

/**
 * Hand tremor, in pixels: the small high-frequency wobble applied to every
 * sampled point, across and along the stroke. Small enough to read as texture
 * rather than as a shape.
 * @type {number}
 */
const TREMOR = 0.8;

/**
 * Roughly how far apart, in pixels, the stroke is sampled. Closer sampling
 * gives the tremor somewhere to show; too close and the curve turns to fuzz.
 * @type {number}
 */
const SAMPLE_SPACING = 240;

/** Sampling bounds per segment, whatever its length. */
const MIN_SAMPLES = 2;
const MAX_SAMPLES = 5;

/**
 * How irregularly the samples are spaced, as a fraction of the even spacing.
 * Evenly spaced points are the main reason a generated curve reads as
 * machine-drawn.
 * @type {number}
 */
const SPACING_JITTER = 0.4;

/**
 * Bounds on the Catmull-Rom tension used to turn sampled points into Beziers.
 * A fixed 1/6 (0.167) is the textbook value and gives uniformly round curves;
 * varying it per control point makes some corners tight and others loose, the
 * way a hand varies pressure and speed. Both bounds sit above the textbook
 * value, so the stroke is fuller and rounder than a plain spline.
 */
const MIN_TENSION = 0.22;
const MAX_TENSION = 0.34;

/**
 * Shapes the swing envelope. At 1 it is a plain sine, whose zero slope at both
 * ends makes the stroke leave and meet each dot ALONG the segment. Below 1 the
 * envelope has an infinite slope at the dots, so the stroke departs almost
 * perpendicular to the segment - which is what produced the spike where two
 * segments met at the same dot.
 * @type {number}
 */
const ENVELOPE_EASE = 1;

/**
 * Smallest angle, in radians, allowed between the two strokes meeting at a
 * shared dot. Below this the path doubles back on itself and reads as a pinch
 * rather than a curve, so the two sampled neighbours are rotated apart about
 * the dot until the corner opens up to it.
 * @type {number}
 */
const MIN_CORNER_ANGLE = (75 * Math.PI) / 180;

/**
 * Longest a control handle may be, as a fraction of its own span. Catmull-Rom
 * derives a handle from the NEIGHBOURING points, so a short span between two
 * long ones gets a handle longer than itself and the curve cusps or loops.
 * @type {number}
 */
const HANDLE_CAP = 0.6;

/**
 * Minimum distance, in pixels, kept between the path and the overlay's edges,
 * so a sampled point never leaves the section.
 * @type {number}
 */
const EDGE_PADDING = 8;

/**
 * How many times the path is redrawn when it crosses itself, and how much the
 * swing shrinks on each redraw. The first retries stay close to full swing, so
 * the line keeps its character; the last is at about a third of it.
 */
const MAX_ATTEMPTS = 8;
const RETRY_DAMPING = 0.85;

/** Chords per Bezier when testing the path for self-crossings. */
const FLATTEN_STEPS = 16;

/**
 * FNV-1a. Turns the product handle into a 32-bit seed, so a given product
 * always gets the same squiggle and two products get different ones.
 *
 * @param {string} value
 * @returns {number}
 */
function hash(value) {
  let result = 2166136261;
  for (let index = 0; index < value.length; index++) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

/**
 * mulberry32: a small deterministic PRNG. Returns a function producing floats
 * in [0, 1).
 *
 * @param {number} seed
 * @returns {() => number}
 */
function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {number} value
 * @returns {number}
 */
function round(value) {
  return Math.round(value * 100) / 100;
}

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {{ start: Point, c1: Point, c2: Point, end: Point }} Curve
 */

/**
 * The `d` attribute for a run of joined cubic Beziers.
 *
 * @param {Curve[]} curves
 * @returns {string}
 */
function pathData(curves) {
  let d = `M ${round(curves[0].start.x)} ${round(curves[0].start.y)}`;
  for (const { c1, c2, end } of curves) {
    d += ` C ${round(c1.x)} ${round(c1.y)}, ${round(c2.x)} ${round(c2.y)}, ${round(end.x)} ${round(end.y)}`;
  }
  return d;
}

/**
 * Whether a run of Beziers crosses itself anywhere. Each curve is flattened
 * into FLATTEN_STEPS chords and every chord is tested against every other one
 * that does not share an end with it - a few thousand cheap tests for a
 * three-dot path.
 *
 * @param {Curve[]} curves
 * @returns {boolean}
 */
function crossesItself(curves) {
  /** @type {Point[]} */
  const line = [curves[0].start];
  for (const { start, c1, c2, end } of curves) {
    for (let step = 1; step <= FLATTEN_STEPS; step++) {
      const t = step / FLATTEN_STEPS;
      const u = 1 - t;
      const a = u * u * u;
      const b = 3 * u * u * t;
      const c = 3 * u * t * t;
      const e = t * t * t;
      line.push({
        x: a * start.x + b * c1.x + c * c2.x + e * end.x,
        y: a * start.y + b * c1.y + c * c2.y + e * end.y,
      });
    }
  }

  /**
   * @param {Point} p
   * @param {Point} q
   * @param {Point} r
   */
  const side = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);

  for (let i = 0; i < line.length - 1; i++) {
    for (let j = i + 2; j < line.length - 1; j++) {
      const [p1, p2, p3, p4] = [line[i], line[i + 1], line[j], line[j + 1]];
      if (side(p1, p2, p3) * side(p1, p2, p4) < 0 && side(p3, p4, p1) * side(p3, p4, p2) < 0) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Signed angle, in (-pi, pi], turning from the arm `pivot -> from` to the arm
 * `pivot -> to`.
 *
 * @param {Point} pivot
 * @param {Point} from
 * @param {Point} to
 * @returns {number}
 */
function angleBetween(pivot, from, to) {
  let difference =
    Math.atan2(to.y - pivot.y, to.x - pivot.x) - Math.atan2(from.y - pivot.y, from.x - pivot.x);
  while (difference > Math.PI) difference -= Math.PI * 2;
  while (difference <= -Math.PI) difference += Math.PI * 2;
  return difference;
}

/**
 * Three note dots joined by a hand-drawn path.
 *
 * The dots are laid out by CSS (see `snippets/notes-path-styles.liquid`); this
 * component only measures where they ended up and draws between them. The path
 * is regenerated - not rescaled - on every resize, so it always terminates
 * exactly on the dot centres however the labels wrapped.
 *
 * The line is static: it is drawn complete as soon as it is measured.
 *
 * @typedef {object} Refs
 * @property {SVGSVGElement} svg - The overlay.
 * @property {SVGPathElement} path - The line itself.
 * @property {HTMLElement[]} dots - One span per rendered note, head to base.
 *
 * @extends Component<Refs>
 */
class NotesPathComponent extends Component {
  requiredRefs = ['svg', 'path', 'dots'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#measure();

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
  }

  /** The PRNG seed, from the product handle. */
  get #seed() {
    return hash(this.dataset.seed ?? '');
  }

  /**
   * ResizeObserver can fire mid-layout; deferring to the next frame keeps the
   * measurement out of the same pass that triggered it.
   */
  #scheduleMeasure() {
    if (this.#frame != null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#measure();
    });
  }

  /**
   * Recomputes the viewBox and the path. The viewBox is set to the overlay's
   * real pixel size so one user unit is one pixel and `stroke-width` means what
   * the setting says at every breakpoint.
   */
  #measure() {
    const { svg, path } = this.refs;
    const box = svg.getBoundingClientRect();

    if (box.width === 0 || box.height === 0) return;

    svg.setAttribute('viewBox', `0 0 ${round(box.width)} ${round(box.height)}`);

    const points = this.#points(box);

    if (points.length < 2) {
      path.removeAttribute('d');
      return;
    }

    path.setAttribute('d', this.#buildPath(points, box));
  }

  /**
   * Dot centres, in overlay-local pixels.
   *
   * @param {DOMRect} box
   * @returns {Point[]}
   */
  #points(box) {
    return this.refs.dots.map((dot) => {
      const rect = dot.getBoundingClientRect();
      return {
        x: rect.left - box.left + rect.width / 2,
        y: rect.top - box.top + rect.height / 2,
      };
    });
  }

  /**
   * Draws the path, and redraws it while it crosses itself.
   *
   * Each segment swings independently, so where the dots form a narrow wedge -
   * the heart dot far to one side on desktop - one segment can dip into the
   * other's corridor and the two strokes cross well away from any dot, where
   * no corner rule can see it. Every redraw continues the same seeded stream,
   * so a product still always gets the same line, and each swings a little
   * less than the last: straight chords between the dots never cross, so the
   * loop converges.
   *
   * @param {Point[]} dots
   * @param {DOMRect} box
   * @returns {string}
   */
  #buildPath(dots, box) {
    const random = mulberry32(this.#seed);
    let curves = this.#draw(dots, box, random, WANDER);

    for (let attempt = 1; attempt < MAX_ATTEMPTS && crossesItself(curves); attempt++) {
      curves = this.#draw(dots, box, random, WANDER * Math.pow(RETRY_DAMPING, attempt));
    }

    return pathData(curves);
  }

  /**
   * Samples each dot-to-dot segment into a run of displaced points, opens up
   * any corner that came out too tight, then smooths the whole list into
   * cubic Beziers.
   *
   * The displacement is deliberately layered, because a single sine of a single
   * amplitude is exactly what a machine-drawn curve looks like:
   *
   *   - two swings at different rates and random phase, both vanishing at the
   *     dots so the stroke still lands on them exactly;
   *   - a tremor across AND along the stroke, so spacing varies too;
   *   - irregular sample positions, so no two curvature changes are evenly
   *     spaced.
   *
   * @param {Point[]} dots
   * @param {DOMRect} box
   * @param {() => number} random
   * @param {number} wander - The primary swing, as a fraction of segment length.
   * @returns {Curve[]}
   */
  #draw(dots, box, random, wander) {
    /** @type {Point[]} */
    const points = [dots[0]];
    /** Indices in `points` that ARE dots, and so must not be moved. */
    const anchors = [0];

    for (let index = 0; index < dots.length - 1; index++) {
      const from = dots[index];
      const to = dots[index + 1];
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy) || 1;
      // Unit vectors along the segment and across it.
      const tx = dx / length;
      const ty = dy / length;
      const nx = -ty;
      const ny = tx;

      const count = clamp(Math.round(length / SAMPLE_SPACING), MIN_SAMPLES, MAX_SAMPLES);

      // The segment's two swings. Both use sin(pi * t)-shaped envelopes, which
      // are zero at t=0 and t=1, so the stroke meets each dot exactly however
      // wildly it wanders in between.
      const primary = (random() * 2 - 1) * length * wander;
      const secondary = (random() * 2 - 1) * length * wander * SECONDARY_SWING;
      const phase = random() * Math.PI * 2;
      const rate = MIN_SWING_RATE + random() * (MAX_SWING_RATE - MIN_SWING_RATE);

      for (let step = 1; step <= count; step++) {
        const even = step / (count + 1);
        const spacing = (random() * 2 - 1) * (SPACING_JITTER / (count + 1));
        const t = clamp(even + spacing, 0.04, 0.96);

        // sin(pi t) is zero AND flat at both ends, so the stroke leaves and
        // meets each dot along the segment rather than across it.
        const envelope = Math.pow(Math.sin(t * Math.PI), ENVELOPE_EASE);
        const swing =
          primary * envelope + secondary * envelope * Math.sin(t * Math.PI * rate + phase);
        const across = swing + (random() * 2 - 1) * TREMOR;
        const along = (random() * 2 - 1) * TREMOR;

        points.push({
          x: clamp(
            from.x + dx * t + nx * across + tx * along,
            EDGE_PADDING,
            box.width - EDGE_PADDING
          ),
          y: clamp(
            from.y + dy * t + ny * across + ty * along,
            EDGE_PADDING,
            box.height - EDGE_PADDING
          ),
        });
      }

      points.push(to);
      anchors.push(points.length - 1);
    }

    this.#openCorners(points, anchors, box);

    return this.#smooth(points, random);
  }

  /**
   * Opens up the corner at every dot the path passes THROUGH.
   *
   * Two segments meeting at a shared dot are generated independently, so
   * nothing stops the second from setting off back alongside the first. The
   * curve then folds into a spike at the dot however round its two halves are.
   * The dot itself is fixed by the layout, so the fix is to rotate its two
   * sampled neighbours apart about it - equally, so the corner stays where the
   * design put it - until they are at least MIN_CORNER_ANGLE apart.
   *
   * @param {Point[]} points
   * @param {number[]} anchors - Indices of the dots within `points`.
   * @param {DOMRect} box
   */
  #openCorners(points, anchors, box) {
    /**
     * @param {Point} point
     * @param {Point} pivot
     * @param {number} angle
     */
    const rotate = (point, pivot, angle) => {
      const dx = point.x - pivot.x;
      const dy = point.y - pivot.y;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      point.x = clamp(pivot.x + dx * cos - dy * sin, EDGE_PADDING, box.width - EDGE_PADDING);
      point.y = clamp(pivot.y + dx * sin + dy * cos, EDGE_PADDING, box.height - EDGE_PADDING);
    };

    // The first and last anchors are the path's ends, not corners.
    for (const index of anchors.slice(1, -1)) {
      const before = points[index - 1];
      const dot = points[index];
      const after = points[index + 1];
      if (!before || !dot || !after) continue;

      // Which way the corner turns is read from the DOTS, never from the
      // sampled arms. Where the dots meet at a sharp angle (the heart dot on
      // desktop, ~33 degrees), the two swings can carry the arms past each
      // other; measured from the arms, that crossed corner would then be
      // pushed open the wrong way and the crossing set into a loop.
      const previousDot = points[anchors[anchors.indexOf(index) - 1]];
      const nextDot = points[anchors[anchors.indexOf(index) + 1]];
      const direction = Math.sign(angleBetween(dot, previousDot, nextDot)) || 1;

      // Both arms measured OUTWARD from the dot, signed so that a positive
      // opening turns the way the dots do: pi is a straight line, 0 a full
      // fold-back, and a negative value means the arms have crossed.
      const opening = direction * angleBetween(dot, before, after);
      if (opening >= MIN_CORNER_ANGLE) continue;

      // Split the shortfall between the two arms, each turning away from the
      // other, so neither segment is distorted more than the other.
      const correction = (MIN_CORNER_ANGLE - opening) / 2;
      rotate(after, dot, direction * correction);
      rotate(before, dot, -direction * correction);
    }
  }

  /**
   * Catmull-Rom through the points, converted to cubic Beziers.
   *
   * The textbook conversion uses a tension of exactly 1/6 on both control
   * points of every span, which is what makes a generated curve read as
   * uniformly, mechanically round. Here each control point gets its own tension
   * from the seeded PRNG, so the stroke tightens and loosens along its length
   * the way a hand does - and the two handles of a span are drawn
   * independently, so spans are asymmetric as well.
   *
   * @param {Point[]} points
   * @param {() => number} random
   * @returns {Curve[]}
   */
  #smooth(points, random) {
    /**
     * One control-point offset: the Catmull-Rom direction, scaled by a random
     * tension, then capped relative to its own span. Without the cap a short
     * span between two long ones overshoots into a cusp or a small loop.
     *
     * @param {number} dx
     * @param {number} dy
     * @param {number} span
     * @returns {Point}
     */
    const handle = (dx, dy, span) => {
      const tension = MIN_TENSION + random() * (MAX_TENSION - MIN_TENSION);
      const x = dx * tension;
      const y = dy * tension;
      const length = Math.hypot(x, y) || 1;
      const cap = span * HANDLE_CAP;
      const scale = length > cap ? cap / length : 1;
      return { x: x * scale, y: y * scale };
    };
    /** @type {Curve[]} */
    const curves = [];

    for (let index = 0; index < points.length - 1; index++) {
      const previous = points[index - 1] ?? points[index];
      const start = points[index];
      const end = points[index + 1];
      const next = points[index + 2] ?? points[index + 1];

      const span = Math.hypot(end.x - start.x, end.y - start.y) || 1;
      const leading = handle(end.x - previous.x, end.y - previous.y, span);
      const trailing = handle(next.x - start.x, next.y - start.y, span);

      curves.push({
        start,
        c1: { x: start.x + leading.x, y: start.y + leading.y },
        c2: { x: end.x - trailing.x, y: end.y - trailing.y },
        end,
      });
    }

    return curves;
  }
}

if (!customElements.get('notes-path-component')) {
  customElements.define('notes-path-component', NotesPathComponent);
}
